import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, ShieldCheck, Star, MapPin, Loader2, AlertTriangle,
  Building2, PoundSterling, ClipboardList, CreditCard,
} from "lucide-react";
import { marketplaceApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, fmtDate, apiError } from "@/lib/format";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Client-side mirror of services/marketplace.calc_commission so the admin
// sees the effect of edits live, before saving.
export function calcCommission(value, cfg) {
  value = Math.max(0, Number(value) || 0);
  const bands = [...(cfg.bands || [])].sort((a, b) => a.upto - b.upto);
  const above = Number(cfg.above_rate) || 0;
  let fee = 0;
  const breakdown = [];
  if (cfg.mode === "flat") {
    let rate = above;
    for (const b of bands) { if (value <= b.upto) { rate = b.rate; break; } }
    fee = (value * rate) / 100;
    breakdown.push({ from: 0, to: value, rate, amount: fee });
  } else {
    let lower = 0;
    for (const b of bands) {
      if (value <= lower) break;
      const upper = Math.min(value, b.upto);
      const portion = Math.max(0, upper - lower);
      if (portion > 0) { const amt = (portion * b.rate) / 100; fee += amt; breakdown.push({ from: lower, to: upper, rate: b.rate, amount: amt }); }
      lower = b.upto;
    }
    if (value > lower) { const portion = value - lower; const amt = (portion * above) / 100; fee += amt; breakdown.push({ from: lower, to: value, rate: above, amount: amt }); }
  }
  const minFee = Number(cfg.min_fee) || 0;
  const minApplied = fee < minFee;
  const feeFinal = Math.max(fee, minFee);
  const eff = value > 0 ? (feeFinal / value) * 100 : 0;
  return { value, fee: feeFinal, raw_fee: fee, min_fee: minFee, min_applied: minApplied, effective_rate: eff, breakdown };
}

const STATUS_TONE = {
  approved: "bg-emerald-100 text-emerald-700",
  applied: "bg-slate-100 text-slate-600",
  under_review: "bg-amber-100 text-amber-700",
  suspended: "bg-orange-100 text-orange-700",
  rejected: "bg-red-100 text-red-700",
};
const STATUS_LABEL = {
  applied: "Applied", under_review: "Under review", approved: "Approved",
  suspended: "Suspended", rejected: "Rejected",
};

export default function MarketplaceTab() {
  return (
    <Tabs defaultValue="commission" className="space-y-4">
      <TabsList>
        <TabsTrigger value="commission" data-testid="mp-tab-commission">Commission rates</TabsTrigger>
        <TabsTrigger value="contractors" data-testid="mp-tab-contractors">Contractors</TabsTrigger>
        <TabsTrigger value="territories" data-testid="mp-tab-territories">Territories</TabsTrigger>
        <TabsTrigger value="leads" data-testid="mp-tab-leads">Leads</TabsTrigger>
        <TabsTrigger value="charges" data-testid="mp-tab-charges">Charges due</TabsTrigger>
      </TabsList>
      <TabsContent value="commission"><CommissionSettings /></TabsContent>
      <TabsContent value="contractors"><ContractorsSection /></TabsContent>
      <TabsContent value="territories"><TerritoriesSection /></TabsContent>
      <TabsContent value="leads"><LeadsSection /></TabsContent>
      <TabsContent value="charges"><CommissionCharges /></TabsContent>
    </Tabs>
  );
}

/* ---------------- Commission settings ---------------- */
function CommissionSettings() {
  const { data, loading, error, reload } = useAsync(() => marketplaceApi.getConfig(), []);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewVal, setPreviewVal] = useState("25000");

  useEffect(() => {
    if (data) setForm({
      min_fee: data.min_fee, mode: data.mode, above_rate: data.above_rate,
      quotes_per_lead_cap: data.quotes_per_lead_cap,
      bands: (data.bands || []).map((b) => ({ ...b })),
    });
  }, [data]);

  const preview = useMemo(() => (form ? calcCommission(parseFloat(previewVal) || 0, form) : null), [form, previewVal]);

  if (loading) return <LoadingState label="Loading commission settings…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!form) return null;

  const setBand = (i, key, val) => setForm((f) => ({ ...f, bands: f.bands.map((b, idx) => idx === i ? { ...b, [key]: val } : b) }));
  const addBand = () => setForm((f) => ({ ...f, bands: [...f.bands, { upto: 0, rate: 0 }] }));
  const removeBand = (i) => setForm((f) => ({ ...f, bands: f.bands.filter((_, idx) => idx !== i) }));

  const save = async () => {
    const bands = form.bands.map((b) => ({ upto: Number(b.upto), rate: Number(b.rate) })).filter((b) => b.upto > 0);
    if (!bands.length) { toast.error("Add at least one value band."); return; }
    setSaving(true);
    try {
      await marketplaceApi.updateConfig({
        min_fee: Number(form.min_fee), mode: form.mode, above_rate: Number(form.above_rate),
        quotes_per_lead_cap: Number(form.quotes_per_lead_cap), bands,
      });
      toast.success("Commission settings saved");
      reload();
    } catch (e) { toast.error(apiError(e, "Could not save")); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]">
      <Card className="p-5" data-testid="mp-commission-form">
        <h3 className="font-semibold text-slate-800">Commission the platform charges the winning contractor</h3>
        <p className="mt-1 text-sm text-slate-500">Charged when the client awards the job. You control every figure here.</p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Calculation</Label>
            <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
              <SelectTrigger data-testid="mp-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="marginal">Marginal (tax-bracket)</SelectItem>
                <SelectItem value="flat">Flat band</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Minimum fee (£)</Label>
            <Input type="number" step="1" min="0" value={form.min_fee} onChange={(e) => setForm((f) => ({ ...f, min_fee: e.target.value }))} data-testid="mp-min-fee" />
          </div>
          <div className="space-y-1.5">
            <Label>Rate above top band (%)</Label>
            <Input type="number" step="0.1" min="0" value={form.above_rate} onChange={(e) => setForm((f) => ({ ...f, above_rate: e.target.value }))} data-testid="mp-above-rate" />
          </div>
          <div className="space-y-1.5">
            <Label>Max contractors per lead</Label>
            <Input type="number" step="1" min="1" value={form.quotes_per_lead_cap} onChange={(e) => setForm((f) => ({ ...f, quotes_per_lead_cap: e.target.value }))} data-testid="mp-cap" />
          </div>
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <Label>Value bands</Label>
            <Button size="sm" variant="outline" onClick={addBand} data-testid="mp-add-band"><Plus className="mr-1 h-3.5 w-3.5" /> Add band</Button>
          </div>
          <div className="space-y-2">
            {form.bands.map((b, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`mp-band-${i}`}>
                <span className="w-20 text-sm text-slate-500">up to £</span>
                <Input type="number" step="1" value={b.upto} onChange={(e) => setBand(i, "upto", e.target.value)} className="w-36" data-testid={`mp-band-upto-${i}`} />
                <Input type="number" step="0.1" value={b.rate} onChange={(e) => setBand(i, "rate", e.target.value)} className="w-24" data-testid={`mp-band-rate-${i}`} />
                <span className="text-sm text-slate-500">%</span>
                <Button size="icon" variant="ghost" className="text-red-500" onClick={() => removeBand(i)} data-testid={`mp-band-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </div>

        <Button className="mt-5" onClick={save} disabled={saving} data-testid="mp-save-config">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save commission settings
        </Button>
      </Card>

      <Card className="p-5" data-testid="mp-commission-preview">
        <h3 className="flex items-center gap-2 font-semibold text-slate-800"><PoundSterling className="h-4 w-4 text-red-600" /> Fee calculator</h3>
        <p className="mt-1 text-sm text-slate-500">Live preview of your current (unsaved) settings.</p>
        <div className="mt-3 space-y-1.5">
          <Label>Job value (£)</Label>
          <Input type="number" step="100" value={previewVal} onChange={(e) => setPreviewVal(e.target.value)} data-testid="mp-preview-value" />
        </div>
        {preview && (
          <div className="mt-4 rounded-xl bg-slate-50 p-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-slate-500">Platform commission</span>
              <span className="text-2xl font-bold text-slate-900" data-testid="mp-preview-fee">{fmtMoney(preview.fee)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-sm text-slate-500">
              <span>Effective rate</span><span>{preview.effective_rate.toFixed(2)}%</span>
            </div>
            {preview.min_applied && <p className="mt-2 text-xs font-medium text-amber-700">Minimum fee applied</p>}
            <div className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-xs text-slate-500">
              {preview.breakdown.map((s, i) => (
                <div key={i} className="flex justify-between">
                  <span>{fmtMoney(s.from)}–{fmtMoney(s.to)} @ {s.rate}%</span><span>{fmtMoney(s.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Contractors ---------------- */
function ContractorsSection() {
  const { data, loading, error, reload } = useAsync(() => marketplaceApi.listContractors(), []);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // contractor object or {} for new
  const alerts = useAsync(() => marketplaceApi.alerts(), []);

  const rows = useMemo(() => {
    let list = data || [];
    if (statusFilter !== "all") list = list.filter((c) => c.status === statusFilter);
    if (q.trim()) list = list.filter((c) => (c.name || "").toLowerCase().includes(q.trim().toLowerCase()));
    return list;
  }, [data, statusFilter, q]);

  const setStatus = async (c, status) => {
    try { await marketplaceApi.updateContractor(c.id, { status }); toast.success(`${c.name} → ${STATUS_LABEL[status]}`); reload(); alerts.reload(); }
    catch (e) { toast.error(apiError(e, "Could not update")); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await marketplaceApi.removeContractor(c.id); toast.success("Deleted"); reload(); alerts.reload(); }
    catch (e) { toast.error(apiError(e, "Could not delete")); }
  };

  return (
    <div className="space-y-4">
      {Array.isArray(alerts.data) && alerts.data.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="mp-alerts">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            {alerts.data.reduce((n, c) => n + c.alerts.length, 0)} document(s) expiring or expired across {alerts.data.length} contractor(s)
          </p>
          <ul className="mt-2 space-y-1 text-sm text-amber-800">
            {alerts.data.slice(0, 6).map((c) => (
              <li key={c.contractor_id}>
                <span className="font-medium">{c.name}</span> — {c.alerts.map((a) => `${a.label} (${a.status === "expired" ? "expired" : a.days_left + "d"})`).join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search contractors…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="mp-contractor-search" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="mp-contractor-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="applied">Applied</SelectItem>
            <SelectItem value="under_review">Under review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <Button onClick={() => setEditing({})} data-testid="mp-add-contractor"><Plus className="mr-1.5 h-4 w-4" /> Add contractor</Button>
        </div>
      </div>

      {loading ? <LoadingState label="Loading contractors…" /> :
        error ? <ErrorState message={error} onRetry={reload} /> :
        rows.length === 0 ? <EmptyState title="No contractors yet" message="Add a fire-door maintenance company to start building your marketplace." /> :
        <div className="grid gap-3">
          {rows.map((c) => <ContractorRow key={c.id} c={c} onStatus={setStatus} onEdit={() => setEditing(c)} onDelete={() => remove(c)} />)}
        </div>}

      {editing !== null && (
        <ContractorDialog contractor={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
}

function expiryFlag(items) {
  const dates = (items || []).map((x) => x.expires_on).filter(Boolean).map((d) => new Date(d));
  if (!dates.length) return null;
  const soonest = new Date(Math.min(...dates.map((d) => d.getTime())));
  const days = Math.round((soonest - new Date()) / 86400000);
  if (days < 0) return { tone: "text-red-600", label: "Expired" };
  if (days < 30) return { tone: "text-amber-600", label: `Expires in ${days}d` };
  return null;
}

function ContractorRow({ c, onStatus, onEdit, onDelete }) {
  const insFlag = expiryFlag(c.insurances);
  return (
    <Card className="p-4" data-testid={`mp-contractor-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-slate-800">{c.name}</h4>
            <Badge className={STATUS_TONE[c.status] || STATUS_TONE.applied}>{STATUS_LABEL[c.status] || c.status}</Badge>
            {typeof c.google_rating === "number" && (
              <span className="inline-flex items-center gap-0.5 text-sm text-amber-600"><Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />{c.google_rating}</span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
            {c.contact_name && <span>{c.contact_name}</span>}
            {c.email && <span>{c.email}</span>}
            {c.phone && <span>{c.phone}</span>}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-slate-400" />
            {(c.territories || []).length ? c.territories.map((t) => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>) : <span className="text-xs text-slate-400">No territory set</span>}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> {(c.insurances || []).length} insurance · {(c.qualifications || []).length} quals · {(c.accreditations || []).length} accreditations</span>
            {insFlag && <span className={`inline-flex items-center gap-1 font-medium ${insFlag.tone}`}><AlertTriangle className="h-3.5 w-3.5" /> Insurance {insFlag.label}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={c.status} onValueChange={(v) => onStatus(c, v)}>
            <SelectTrigger className="w-36" data-testid={`mp-contractor-status-${c.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="applied">Applied</SelectItem>
              <SelectItem value="under_review">Under review</SelectItem>
              <SelectItem value="approved">Approve</SelectItem>
              <SelectItem value="suspended">Suspend</SelectItem>
              <SelectItem value="rejected">Reject</SelectItem>
            </SelectContent>
          </Select>
          <Button size="icon" variant="ghost" onClick={onEdit} data-testid={`mp-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="text-red-500" onClick={onDelete} data-testid={`mp-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    </Card>
  );
}

function RowList({ label, rows, columns, onChange, testId }) {
  const add = () => onChange([...(rows || []), columns.reduce((a, c) => ({ ...a, [c.key]: "" }), {})]);
  const set = (i, key, val) => onChange(rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const del = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <Label>{label}</Label>
        <Button size="sm" variant="outline" onClick={add} data-testid={`${testId}-add`}><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
      </div>
      {(rows || []).length === 0 && <p className="text-xs text-slate-400">None added.</p>}
      <div className="space-y-2">
        {(rows || []).map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`${testId}-${i}`}>
            {columns.map((col) => (
              <Input key={col.key} placeholder={col.placeholder} type={col.type || "text"} value={r[col.key] ?? ""} onChange={(e) => set(i, col.key, e.target.value)} className={col.className || "flex-1 min-w-[120px]"} />
            ))}
            <Button size="icon" variant="ghost" className="text-red-500" onClick={() => del(i)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContractorDialog({ contractor, onClose, onSaved }) {
  const isNew = !contractor.id;
  const [f, setF] = useState(() => ({
    name: "", contact_name: "", email: "", phone: "", address: "", postcode: "",
    company_number: "", vat_number: "", website: "", google_reviews_url: "", notes: "",
    territoriesText: (contractor.territories || []).join(", "),
    social: contractor.social_links || {},
    insurances: contractor.insurances || [],
    qualifications: contractor.qualifications || [],
    accreditations: contractor.accreditations || [],
    ...contractor,
    google_rating: contractor.google_rating ?? "",
    google_reviews_count: contractor.google_reviews_count ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setSocial = (k) => (e) => setF((s) => ({ ...s, social: { ...s.social, [k]: e.target.value } }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Company name is required"); return; }
    const cleanRows = (rows, numericKeys = []) => (rows || []).map((r) => {
      const o = { ...r };
      numericKeys.forEach((k) => { o[k] = (o[k] === "" || o[k] == null) ? null : Number(o[k]); });
      return o;
    }).filter((r) => Object.entries(r).some(([k, v]) => (numericKeys.includes(k) ? v != null : String(v ?? "").trim() !== "")));
    const payload = {
      name: f.name.trim(), contact_name: f.contact_name, email: f.email, phone: f.phone,
      address: f.address, postcode: f.postcode, company_number: f.company_number, vat_number: f.vat_number,
      website: f.website, notes: f.notes,
      territories: (f.territoriesText || "").split(",").map((t) => t.trim()).filter(Boolean),
      social_links: Object.fromEntries(Object.entries(f.social || {}).filter(([, v]) => (v || "").trim())),
      google_rating: f.google_rating === "" ? null : Number(f.google_rating),
      google_reviews_url: f.google_reviews_url,
      google_reviews_count: f.google_reviews_count === "" ? null : Number(f.google_reviews_count),
      insurances: cleanRows(f.insurances, ["cover_amount"]),
      qualifications: cleanRows(f.qualifications),
      accreditations: cleanRows(f.accreditations),
    };
    setSaving(true);
    try {
      if (isNew) await marketplaceApi.createContractor(payload);
      else await marketplaceApi.updateContractor(contractor.id, payload);
      toast.success(isNew ? "Contractor added" : "Contractor saved");
      onSaved();
    } catch (e) { toast.error(apiError(e, "Could not save")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="mp-contractor-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add contractor" : "Edit contractor"}</DialogTitle>
          <DialogDescription>Full company details, insurance, qualifications and coverage. Approve them to receive leads.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Company name *"><Input value={f.name} onChange={upd("name")} data-testid="mp-c-name" /></Field>
            <Field label="Contact name"><Input value={f.contact_name} onChange={upd("contact_name")} /></Field>
            <Field label="Email"><Input type="email" value={f.email} onChange={upd("email")} /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={upd("phone")} /></Field>
            <Field label="Address"><Input value={f.address} onChange={upd("address")} /></Field>
            <Field label="Postcode"><Input value={f.postcode} onChange={upd("postcode")} /></Field>
            <Field label="Company number"><Input value={f.company_number} onChange={upd("company_number")} /></Field>
            <Field label="VAT number"><Input value={f.vat_number} onChange={upd("vat_number")} /></Field>
          </div>

          <Field label="Territories covered (postcode districts, comma-separated e.g. SW1A, M1, EC1)">
            <Input value={f.territoriesText} onChange={upd("territoriesText")} placeholder="SW1A, M1, EC1" data-testid="mp-c-territories" />
          </Field>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Website"><Input value={f.website} onChange={upd("website")} /></Field>
            <Field label="Google rating"><Input type="number" step="0.1" min="0" max="5" value={f.google_rating} onChange={upd("google_rating")} /></Field>
            <Field label="Google reviews #"><Input type="number" value={f.google_reviews_count} onChange={upd("google_reviews_count")} /></Field>
          </div>
          <Field label="Google reviews link"><Input value={f.google_reviews_url} onChange={upd("google_reviews_url")} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Facebook"><Input value={f.social?.facebook || ""} onChange={setSocial("facebook")} /></Field>
            <Field label="Instagram"><Input value={f.social?.instagram || ""} onChange={setSocial("instagram")} /></Field>
            <Field label="LinkedIn"><Input value={f.social?.linkedin || ""} onChange={setSocial("linkedin")} /></Field>
            <Field label="X / Twitter"><Input value={f.social?.x || ""} onChange={setSocial("x")} /></Field>
          </div>

          <RowList label="Insurance (up to date)" testId="mp-c-insurance" rows={f.insurances} onChange={(v) => setF((s) => ({ ...s, insurances: v }))}
            columns={[{ key: "type", placeholder: "Type e.g. Public Liability" }, { key: "insurer", placeholder: "Insurer" }, { key: "cover_amount", placeholder: "Cover £", type: "number", className: "w-28" }, { key: "expires_on", placeholder: "Expires", type: "date", className: "w-40" }]} />

          <RowList label="Fire-door training qualifications" testId="mp-c-quals" rows={f.qualifications} onChange={(v) => setF((s) => ({ ...s, qualifications: v }))}
            columns={[{ key: "name", placeholder: "Qualification e.g. FDIS Inspector" }, { key: "body", placeholder: "Training body" }, { key: "expires_on", placeholder: "Expires", type: "date", className: "w-40" }]} />

          <RowList label="Accreditations / registered schemes" testId="mp-c-accred" rows={f.accreditations} onChange={(v) => setF((s) => ({ ...s, accreditations: v }))}
            columns={[{ key: "scheme", placeholder: "Scheme e.g. BM TRADA Q-Mark, FIRAS" }, { key: "reference", placeholder: "Reference" }, { key: "expires_on", placeholder: "Expires", type: "date", className: "w-40" }]} />

          <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={upd("notes")} /></Field>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="mp-c-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {isNew ? "Add contractor" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Territories (subscriptions) ---------------- */
const COMP_OPTIONS = [
  { label: "None", value: "0" },
  { label: "1 area", value: "1" },
  { label: "3 areas", value: "3" },
  { label: "5 areas", value: "5" },
  { label: "Unlimited", value: "-1" },
];

function compLabel(sub) {
  if (!sub?.active) return "—";
  if (sub.allowance === null) return "Unlimited";
  return `${sub.allowance} area${sub.allowance === 1 ? "" : "s"}`;
}

function TerritoriesSection() {
  const { data, loading, error, reload } = useAsync(() => marketplaceApi.territorySubscriptions(), []);
  const setComp = async (c, value) => {
    try {
      await marketplaceApi.updateContractor(c.id, { complimentary_territories: Number(value) });
      toast.success(`Updated ${c.name}`);
      reload();
    } catch (e) { toast.error(apiError(e, "Could not update")); }
  };
  if (loading) return <LoadingState label="Loading subscriptions…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const rows = data?.contractors || [];
  const s = data?.summary || {};
  return (
    <div className="space-y-4" data-testid="mp-territories">
      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="p-4" data-testid="mp-terr-active"><p className="text-sm text-slate-500">Active subscriptions</p><p className="text-2xl font-bold text-slate-900">{s.active || 0}</p></Card>
        <Card className="p-4"><p className="text-sm text-slate-500">Postcode areas sold</p><p className="text-2xl font-bold text-slate-900">{s.areas_sold || 0}{s.unlimited ? ` + ${s.unlimited} unlimited` : ""}</p></Card>
        <Card className="p-4"><p className="text-sm text-slate-500">Contractors</p><p className="text-2xl font-bold text-slate-900">{s.total || 0}</p></Card>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No contractors yet" message="Territory subscriptions appear once contractors sign up." />
      ) : (
        <div className="grid gap-3">
          {rows.map((c) => {
            const sub = c.subscription || {};
            const compValue = c.subscription?.source === "complimentary" ? (sub.allowance === null ? "-1" : String(sub.allowance)) : "0";
            return (
              <Card key={c.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`mp-terr-${c.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-semibold text-slate-800">
                    <CreditCard className="h-4 w-4 text-slate-400" />
                    {c.name}
                    <Badge className={`border-0 ${sub.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {sub.active ? (sub.source === "complimentary" ? "Complimentary" : "Subscribed") : "No plan"}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    Allowance {compLabel(sub)} · {c.territories_used} area{c.territories_used === 1 ? "" : "s"} used
                    {sub.price_string ? ` · ${sub.price_string}/yr` : ""}
                    {sub.store ? ` · ${sub.store === "play_store" ? "Google Play" : sub.store === "app_store" ? "Apple" : sub.store}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Comp</span>
                  <Select value={compValue} onValueChange={(v) => setComp(c, v)}>
                    <SelectTrigger className="w-36" data-testid={`mp-terr-comp-${c.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COMP_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- Leads ---------------- */
const LEAD_TONE = {
  draft: "bg-slate-100 text-slate-600",
  self_assigned: "bg-blue-100 text-blue-700",
  published: "bg-emerald-100 text-emerald-700",
  awarded: "bg-purple-100 text-purple-700",
  closed: "bg-slate-200 text-slate-500",
};
const LEAD_LABEL = { draft: "Draft", self_assigned: "Doing in-house", published: "On marketplace", awarded: "Awarded", closed: "Closed" };

function LeadsSection() {
  const { data, loading, error, reload } = useAsync(() => marketplaceApi.listLeads(), []);
  if (loading) return <LoadingState label="Loading leads…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const rows = data || [];
  if (!rows.length) return <EmptyState title="No leads yet" message="Leads appear when a tenant puts a remedial quote to the marketplace." />;
  return (
    <div className="grid gap-3">
      {rows.map((l) => (
        <Card key={l.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`mp-lead-${l.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-slate-400" />
              <h4 className="truncate font-semibold text-slate-800">{l.title || "Remedial works"}</h4>
              <Badge className={LEAD_TONE[l.status] || LEAD_TONE.draft}>{LEAD_LABEL[l.status] || l.status}</Badge>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{l.building_name || "—"}{l.building_postcode ? ` · ${l.building_postcode}` : ""}</span>
              <span>Created {fmtDate(l.created_at)}</span>
              {l.status === "published" && <span>{(l.matched_contractor_ids || []).length} contractor(s) matched</span>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-slate-900">{fmtMoney(l.status === "awarded" ? l.awarded_amount : l.estimated_value)}</p>
            <p className="text-xs text-slate-500">{l.status === "awarded" ? "Awarded" : "Est."} · commission {fmtMoney(l.commission?.fee)}</p>
          </div>
        </Card>
      ))}
    </div>
  );
}

const CHARGE_TONE = { due: "bg-amber-100 text-amber-800", invoiced: "bg-blue-100 text-blue-700", paid: "bg-emerald-100 text-emerald-800", waived: "bg-slate-100 text-slate-500" };

function CommissionCharges() {
  const { data, loading, error, reload } = useAsync(() => marketplaceApi.listCharges(), []);
  if (loading) return <LoadingState label="Loading commission…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const rows = data || [];
  const dueTotal = rows.filter((c) => c.status === "due").reduce((n, c) => n + (c.fee || 0), 0);
  const dueCount = rows.filter((c) => c.status === "due").length;
  const setStatus = async (c, status) => {
    try { await marketplaceApi.updateCharge(c.id, status); toast.success("Updated"); reload(); }
    catch (e) { toast.error(apiError(e, "Could not update")); }
  };
  if (!rows.length) return <EmptyState title="No commission yet" message="Commission is raised automatically when a building owner appoints a contractor." />;
  return (
    <div className="space-y-3" data-testid="mp-charges">
      <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-semibold text-red-800">
        Outstanding commission (due): {fmtMoney(dueTotal)} across {dueCount} job(s)
      </div>
      {rows.map((c) => (
        <Card key={c.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`mp-charge-${c.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-semibold text-slate-800">
              {c.contractor_name || "Contractor"}
              <Badge className={`${CHARGE_TONE[c.status] || ""} border-0`}>{c.status}</Badge>
            </div>
            <p className="text-sm text-slate-500">{c.lead_title || c.building_name || "Remedial works"} · works {fmtMoney(c.works_value)} · {fmtDate(c.created_at)}</p>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-lg font-bold text-slate-900">{fmtMoney(c.fee)}</p>
            <Select value={c.status} onValueChange={(v) => setStatus(c, v)}>
              <SelectTrigger className="w-32" data-testid={`mp-charge-status-${c.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="due">Due</SelectItem>
                <SelectItem value="invoiced">Invoiced</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="waived">Waived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
